import { apiClient } from './client'
import type {
  GA4ConfigResponse,
  GA4ValidationResponse
} from '@/types/ga4'

export class GA4Service {
  /**
   * Get GA4 configuration for a store
   */
  async getConfig(storeId: string): Promise<GA4ConfigResponse> {
    try {
      const response = await apiClient.get<{success: boolean, data: any}>(
        `/api/stores/${storeId}/ga4-config`
      )

      // Handle the nested API response format
      if (response.success && response.data && response.data.configured) {
        return {
          success: true,
          config: {
            store_id: response.data.store_id,
            property_id: response.data.property_id,
            configured: response.data.configured
          },
          message: response.data.message
        }
      } else {
        return {
          success: false,
          message: response.data?.message || 'GA4 not configured'
        }
      }
    } catch (error: any) {
      console.error('Failed to get GA4 config:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to load GA4 configuration'
      }
    }
  }

  /**
   * Save GA4 configuration for a store (simplified - only property ID needed)
   */
  async saveConfig(storeId: string, propertyId: string): Promise<GA4ConfigResponse> {
    try {
      const response = await apiClient.post<{success: boolean, data: any}>(
        `/api/stores/${storeId}/ga4-config`,
        { property_id: propertyId }
      )

      // Handle the nested API response format
      if (response.success && response.data) {
        return {
          success: true,
          config: {
            store_id: response.data.store_id,
            property_id: response.data.property_id,
            configured: true
          },
          message: response.data.message
        }
      } else {
        return {
          success: false,
          message: response.data?.message || 'Failed to save GA4 configuration'
        }
      }
    } catch (error: any) {
      console.error('Failed to save GA4 config:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to save GA4 configuration'
      }
    }
  }

  /**
   * Delete GA4 configuration for a store
   */
  async deleteConfig(storeId: string): Promise<GA4ConfigResponse> {
    try {
      const response = await apiClient.delete<{success: boolean, data: any}>(
        `/api/stores/${storeId}/ga4-config`
      )

      // Handle the nested API response format
      if (response.success) {
        return {
          success: true,
          message: response.data?.message || 'GA4 configuration deleted'
        }
      } else {
        return {
          success: false,
          message: response.data?.message || 'Failed to delete GA4 configuration'
        }
      }
    } catch (error: any) {
      console.error('Failed to delete GA4 config:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete GA4 configuration'
      }
    }
  }

  /**
   * Test GA4 access for a store and validate e-commerce events
   */
  async testAccess(storeId: string): Promise<GA4ValidationResponse> {
    try {
      const response = await apiClient.get<{success: boolean, data: any}>(
        `/api/stores/${storeId}/ga4-config/validate`
      )

      const data = response.data || {}

      return {
        success: response.success,
        valid: response.success,
        message: data.message,
        store_id: data.store_id,
        property_id: data.property_id,
        sessions_last_7_days: data.sessions_last_7_days,
        test_date: data.test_date,
        // E-commerce events validation
        is_configured: data.is_configured,
        missing_events: data.missing_events || [],
        has_data: data.has_data,
        events_found: data.events_found || [],
        validation_message: data.validation_message
      }
    } catch (error: any) {
      console.error('Failed to test GA4 access:', error)
      const errorMessage = error.response?.data?.error?.message || error.response?.data?.message || 'Failed to test GA4 access'
      return {
        success: false,
        valid: false,
        error: errorMessage,
        is_configured: false,
        missing_events: [],
        has_data: false,
        events_found: []
      }
    }
  }

  /**
   * GA4 analytics data is now integrated into the layered analytics system.
   * Use experimentService.getExperimentFunnel() or storeService.getStoreAnalytics()
   * to get analytics data that includes GA4 metrics when available.
   *
   * The response will include:
   * - tnx_analytics: TN-Xperiment native analytics (always present)
   * - ga4_analytics: GA4 metrics (when GA4 is configured)
   * - enhanced_analytics: Cross-validated and enhanced insights (when both available)
   */



  /**
   * Validate GA4 property ID format
   */
  validatePropertyId(propertyId: string): boolean {
    // GA4 property IDs are numeric strings (e.g., "123456789")
    return /^\d+$/.test(propertyId.trim())
  }

}

// Export singleton instance
export const ga4Service = new GA4Service()